import { Component, DestroyRef, Inject, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DatePipe, DOCUMENT } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FileRemoveEvent, FileSelectEvent, FileUpload, FileUploadModule } from 'primeng/fileupload';

import { FeedbackVersion, FeedbackFileInfo, SupportHubCommentEntry, SupportHubRequest, SupportHubUserLookup, clarificationsEntry } from '../support-hub.models';
import { SupportHubStore } from '../support-hub.store';
import { MfgappsApiService } from '../../../services/mfgapps-api.service';
import { MfgappsEventService } from '../../../services/mfgapps-event.service';
import { UserModel } from 'src/app/models/userModel';

type SupportHubDetailTab = 'comments' | 'activity' | 'teamthread' | 'clarifications';
const DEFAULT_ACTIVE_TAB: SupportHubDetailTab = 'comments';

@Component({
  selector: 'app-support-hub-detail',
  standalone: true,
  imports: [RouterLink, DatePipe, FormsModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, FileUploadModule],
  templateUrl: './support-hub-detail.component.html',
  styleUrl: './support-hub-detail.component.scss'
})
export class SupportHubDetailComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private static readonly TABS: readonly SupportHubDetailTab[] = ['comments', 'activity', 'teamthread', 'clarifications'];
  private readonly attachmentPreviewUrlById = new Map<number, string>();
  private readonly attachmentPreviewUrlByLocalKey = new Map<string, string>();

  SupportHubStore = SupportHubStore;
  currentRole = SupportHubStore.ROLE_USER;
  hasRouteRequestId = false;
  request!: SupportHubRequest;
  feedbackId!: number;
  loader: { isLoading: boolean; message: string | null } = { isLoading: false, message: null };
  activeTab: SupportHubDetailTab = DEFAULT_ACTIVE_TAB;
  loadingFollowers = false;
  loadingLikers = true;
  @ViewChild(FileUpload) fileUploader!: FileUpload;
  feedbackFiles: FeedbackFileInfo[] = [];
  //MRH-9667 For now we show only submitted date and not the time.
  //dateFormat = 'MMM d, y · h:mm a';
  dateFormat = 'MMM d, y';

  publicComment = '';
  teamThreadNote = '';
  readonly commentsRowsPerPage = 10;
  loadingPublicComments = true;
  loadingPrivateNotes = true;
  isCommentActionProgress = false;
  editingPublicCommentId: number | null = null;
  editingPublicCommentText = '';
  editingPrivateCommentId: number | null = null;
  editingPrivateCommentText = '';
  publicCommentsPage = 1;
  publicCommentsTotalPages = 1;
  publicCommentsTotalCount = 0;
  privateCommentsPage = 1;
  privateCommentsTotalPages = 1;
  privateCommentsTotalCount = 0;
  readonly activityRowsPerPage = 10;
  loadingActivityLogs = true;
  activityLogsPage = 1;
  activityLogsTotalPages = 1;
  activityLogsTotalCount = 0;
  clarificationsPage = 1;
  clarificationsTotalCount = 0;
  readonly clarificationsRowsPerPage = 5;
  clarificationsTotalPages = 1;
  
  validationAssignee?: number;
  teamAssignee?: number;
  assigned_user?: number;
  validationUsers: SupportHubUserLookup[] = [];
  teamAssignees: SupportHubUserLookup[] = [];
  clarificationUsers: SupportHubUserLookup[] = [];
  loadingValidationUsers = true;
  loadingTeamAssignees = true;
  selectedStatusId?: number | null;
  selectedPriorityId?: number | null;
  selectedVersionId?: number | null;
  selectedUserNoteId?: number | null;
  versionsList: FeedbackVersion[] = [];
  loadingVersions = true;
  loadingClarificationUsers = true;
  loadingClarifications = true;
  // Initial values for change detection
  initialStatusId?: number | null;
  initialPriorityId?: number;
  initialVersionId?: number;
  initialAssignedUserId?: number;
  initialValidatedUserId?: number;
  clarificationNote = '';
  showRequestClarificationPanel = false;
  showSubmitClarificationPanel = false;
  validationNote = '';
  showRequestValidationPanel = false;
  showConfirmValidationPanel = false;
  clarificationsEntry: clarificationsEntry[] = [];
  buttonRequestClarificationDisabled = false;
  readonly user!: UserModel;
  disabledButtonSubmit = false;
  isClosedOrCompleted = false;

  constructor(
    private readonly route: ActivatedRoute, private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document, public readonly store: SupportHubStore,
    private readonly apiService: MfgappsApiService, private readonly eventService: MfgappsEventService) {
       this.user = eventService.getActiveAccount();
    }

  ngOnInit(): void {
    this.store.currentUserRoleSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(role => {
      this.currentRole = role;
    });
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const tab = params.get('tab');
      if (this.isValidTab(tab)) {
        this.activeTab = tab;
      }
    });
    this.initializeRequestDetails();
  }

  private initializeRequestDetails(): void {
    const requestId = this.route.snapshot.paramMap.get('id');
    this.hasRouteRequestId = !!requestId;
    if (!requestId) {
      return;
    }
    void this.loadRequestDetails(requestId);
  }

  private async loadRequestDetails(requestId: string): Promise<void> {
    this.setLoader('Loading ticket details...');
    try {
      const companyList = await this.eventService.getCorporateHierarchy();
      const response = await this.apiService.getFeedbackEntryDetails(requestId);
      if (response.result) {
        this.isClosedOrCompleted = this.store.isStatus('Closed', response.result.status_id) || this.store.isStatus('Completed', response.result.status_id);
        this.request = {
          likedByUsers: [],
          activityLog: [],
          teamThreadComments: [],
          publicComments: [],
          ...response.result
        };
        this.feedbackId = this.request.feedback_id ?? 0;
        this.feedbackFiles = [...(response.result.files ?? [])];
        this.loadAttachmentPreviews().catch((error: any) => {
          console.info('Failed to load attachment previews:', error);
        });
        this.store.setCompanyPlantShopLabels(companyList, this.request);
        // Store initial and selected values for change detection
        this.selectedStatusId = this.initialStatusId = this.request.status_id;
        this.selectedPriorityId = this.initialPriorityId = this.request.priority_id;
        this.selectedVersionId = this.initialVersionId = this.request.version_id;
        this.teamAssignee = this.initialAssignedUserId = this.request.assigned_user || this.request.requested_user;
        this.validationAssignee = this.initialValidatedUserId = this.request.validated_user;
        this.syncClarificationsFromApiData(this.request.feedback_id ?? 0);
        this.loadingClarifications = false;

        if (this.isClosedOrCompleted) {
          this.eventService.showSnackBar('“No changes can be made to this ticket', 'warn');
        }
        // Load versions based on ticket's usecase_id independently without blocking UI.
        this.loadVersions().catch((error: any) => {
          console.info('Failed to load versions:', error);
        });
        // Load likers independently without blocking UI.
        this.loadLikers().catch((error: any) => {
          console.info('Failed to load liked users:', error);
        });
        // Load followers independently without blocking UI.
        // this.loadFollowers().catch((error: any) => {
        //  console.info('Failed to load following users:', error);
        // });
        // Load comments independently without blocking UI.
        this.loadPublicComments().catch((error: any) => {
          console.info('Failed to load public comments:', error);
        });
        // Load private notes independently without blocking UI.
        this.loadTeamThreadComments().catch((error: any) => {
          console.info('Failed to load team thread comments:', error);
        });
        // Load validation user list independently without blocking UI.
        this.loadValidationUsers().catch((error: any) => {
          console.info('Failed to load validation users:', error);
        });
        // // Load developers list independently without blocking UI.
        // this.loadTeamAssignees().catch((error: any) => {
        //   console.info('Failed to load team assignees:', error);
        // });
        // Load activity logs independently without blocking UI.
        this.loadActivityLogs().catch((error: any) => {
          console.info('Failed to load activity logs:', error);
        });
        // Scroll to fragment (e.g. #actions or #threads) after DOM renders
        const fragment = this.route.snapshot.fragment;
        if (fragment) {
          setTimeout(() => {
            this.document.getElementById(fragment)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        }
      }
      else {
        throw new Error('Failed to load request details');
      }
    }
    finally {
      this.resetLoader();
    }
  }

  private isValidTab(tab: string | null): tab is SupportHubDetailTab {
    return tab != null && SupportHubDetailComponent.TABS.includes(tab as SupportHubDetailTab);
  }

  private loadSendNoteUsers(): void {
    if (!this.canAssignValidator()) {
      this.clarificationUsers = [];
      return;
    }
    this.loadingClarificationUsers = true;
    try {
      this.clarificationUsers = this.validationUsers;
    }
    finally {
      this.loadingClarificationUsers = false;
    }
  }

  private async loadVersions(): Promise<void> {
    if (!this.canAssignVersion()) {
      this.versionsList = [];
      return;
    }
    this.setLoader('Loading versions...');
    this.loadingVersions = true;
    try {
      await this.store.loadLookup();
      const response = await this.apiService.getFeedbackVersions(this.request.usecase_id);
      const allowedStatusIds = this.store.getAssignableVersionStatusIds();

      const versions = Array.isArray(response.result) ? response.result : [];
      this.versionsList = versions.filter((version: FeedbackVersion) =>
        version.version_status_id != null && allowedStatusIds.has(version.version_status_id)
      );
      this.request.version = this.versionsList.find(v => v.version_id === this.request.version_id)?.version_name;
    }
    finally {
      this.loadingVersions = false;
      this.resetLoader();
    }
  }
  
  get isCommentSectionBusy(): boolean {
    return this.loadingPublicComments || this.loadingPrivateNotes || this.isCommentActionProgress;
  }

  get canPaginatePublicComments(): boolean {
    return this.publicCommentsTotalPages > 1;
  }

  get canPaginatePrivateComments(): boolean {
    return this.privateCommentsTotalPages > 1;
  }

  get canPaginateActivityLogs(): boolean {
    return this.activityLogsTotalPages > 1;
  }

  get canPaginateClarifications(): boolean {
    return this.clarificationsTotalPages > 1;
  }

  getStatusLabel(): string {
    return this.store.getStatusNameById(this.request.status_id, 'Unknown');
  }

  getCriticalityLabel(): string {
    return this.store.getCriticalityNameById(this.request.rank_id);
  }

  getUseCaseLabel(): string {
    return this.store.getUseCaseNameById(this.request.usecase_id);
  }

  getWorkCategoryLabel(): string {
    return this.store.getWorkCategoryNameById(this.request.workcategory_id);
  }

  getRequestTypeLabel(): string {
    return this.store.getRequestTypeNameByIds(this.request.usecase_id, this.request.workcategorytype_id);
  }

  getApplicationLabel(): string {
    return this.store.getApplicationNameById(this.request.usecase_id, this.request.application_id);
  }

  getPriorityLabel(): string {
    return this.store.getPriorityNameById(this.request.priority_id);
  }

  getLikedUsersTooltip(): string {
    const likedUsers = this.request.likedByUsers ?? [];
    return likedUsers.length > 0 ? likedUsers.map(user => user.user_name).join('\n') : 'No likes yet';
  }

  /**
   * PO: Close ticket as Duplicate or Other
   */
  closeTicket(reason: 'Duplicate' | 'Other') {
    this.store.closeTicket(this.request, reason);
  }

  /**
   * Team: Request clarification
   */
  async requestClarification() {
    const note = this.clarificationNote.trim();
    if (!note) {
      return;
    }

    await this.store.transitionStatus(
      this.request,
      this.store.statusIds.NEEDS_CLARIFICATION,
      1,
      this.clarificationNote,
      this.initialAssignedUserId,
      this.request.requested_user_name,
      this.request.requested_user_name
    );
    this.selectedStatusId = this.initialStatusId = this.request.status_id;
    this.clarificationNote = '';
    this.showRequestClarificationPanel = false;

    this.syncClarificationsFromApiData(this.request.feedback_id ?? 0);
  }

  /**
   * Team: Send for UAT/Validation
   */
  sendForValidation() {
    this.store.transitionStatus(
      this.request,
      this.store.statusIds.UAT_VALIDATING,
      1,
      this.validationNote || 'Ready for validation',
      this.initialAssignedUserId,
      this.request.validated_user_name,
      this.request.validated_user_name
    );
    this.validationNote = '';
    this.showRequestValidationPanel = false;
  }

  /**
   * Team: Mark as Deploying
   */
  markDeploying() {
    this.store.transitionStatus(this.request, this.store.statusIds.DEPLOYING, 1, 'Approved for deployment');
  }

  /**
   * Team: Mark as Complete
   */
  markComplete() {
    this.store.transitionStatus(this.request, this.store.statusIds.COMPLETE,1, 'Work completed and deployed');
  }

  /**
   * User: ification response
   */
  async submitClarificationResponse() {
    const responseNote = this.clarificationNote.trim();
    if (!responseNote) {
      return;
    }

    await this.store.transitionStatus(
      this.request,
      this.store.statusIds.NEEDS_CLARIFICATION,
      2,
      responseNote,
      this.initialValidatedUserId,
      this.request.requested_user_name,
      this.request.requested_user_name
    );

    this.selectedStatusId = this.initialStatusId = this.request.status_id;
    this.teamAssignee = this.request.contractor_user;
    this.clarificationNote = '';
    this.syncClarificationsFromApiData(this.request.feedback_id ?? 0);
  }

  get paginatedClarifications(): clarificationsEntry[] {
    const start = (this.clarificationsPage - 1) * this.clarificationsRowsPerPage;
    const end = start + this.clarificationsRowsPerPage;
    return this.clarificationsEntry.slice(start, end);
  }

  async goToPreviousClarificationsPage(): Promise<void> {
    if (this.clarificationsPage > 1 && !this.loadingClarifications) {
      this.clarificationsPage -= 1;
    }
  }

  async goToNextClarificationsPage(): Promise<void> {
    if (this.clarificationsPage < this.clarificationsTotalPages && !this.loadingClarifications) {
      this.clarificationsPage += 1;
    }
  }

  /**
   * User: Validate work (approve)
   */
  approveValidation() {
    if (!this.canConfirmValidation()) {
      return;
    }
    this.store.transitionStatus(this.request, this.store.statusIds.UAT_COMPLETE,1, 'Validation approved');
  }

  /**
   * User: Reject validation (needs more work)
   */
  rejectValidation() {
    if (!this.canConfirmValidation() || !this.validationNote.trim()) {
      return;
    }
    this.store.transitionStatus(
      this.request,
      this.store.statusIds.IN_PROGRESS,
      3,
      `Validation rejected: ${this.validationNote}`,
    );
    this.validationNote = '';
  }

  canConfirmValidation(): boolean {
    return this.store.isAssignedValidator(this.request)
      && this.store.isStatus(this.request.status_id, this.store.statusIds.UAT_VALIDATING);
  }

  canSendForValidation(): boolean {
    return this.store.isStatus(this.request.status_id, this.store.statusIds.IN_PROGRESS) && this.request.validated_user != null;
  }

  canSubmitClarification() {
    return this.store.isStatus(this.request.status_id, this.store.statusIds.NEEDS_CLARIFICATION);
  }

  isDeveloperRole(): boolean {
    return this.currentRole === SupportHubStore.ROLE_DEVELOPER;
  }

  isAdminRole(): boolean {
    return this.store.isAdminRoleName(this.currentRole);
  }

  canAssignVersion(): boolean {
    return this.isAdminRole();
  }

  canAssignTeamMember(): boolean {
    return this.isAdminRole();
  }

  canUpdateAnyStatus(): boolean {
    return this.isDeveloperRole() || this.isAdminRole();
  }

  canAssignValidator(): boolean {
    return this.isDeveloperRole() || this.isAdminRole();
  }

  canUseTeamThread(): boolean {
    return this.isDeveloperRole() || this.isAdminRole();
  }

  canManageAttachments(): boolean {
    return this.isDeveloperRole() || this.isAdminRole()
      || this.store.isAssignedValidator(this.request) || this.store.isTicketSubmitter(this.request);
  }

  canEditTicket(): boolean {
    return this.store.isStatus(this.request.status_id, this.store.statusIds.NEW)
      && (this.store.isTicketSubmitter(this.request) || this.isAdminRole());
  }

  async toggleLike(): Promise<void> {
    if (this.loader.isLoading || this.loadingLikers) {
      return;
    }
    const likeMsg = this.request.is_liked ? 'Removing like on' : 'Liking';
    this.setLoader(`${likeMsg} ticket...`);
    try {
      if (await this.store.toggleTicketLike(this.request)) {
        this.loadActivityLogs();
      }
    }
    finally {
      this.resetLoader();
    }
  }

  async loadLikers(): Promise<void> {
    this.loadingLikers = true;
    try {
      const response = await this.apiService.getLikedUsers(this.feedbackId);
      this.request.likedByUsers = Array.isArray(response.result) ? response.result : [];
      this.request.is_liked = this.request.likedByUsers?.some(user => user.user_name.startsWith(this.store.currentUser)) ?? false;
    }
    catch (error) {
      this.request.likedByUsers = [];
      this.request.is_liked = false;
      throw error;
    }
    finally {
      this.loadingLikers = false;
    }
  }

  async toggleFollowTicket(): Promise<void> {
    if (this.loader.isLoading || this.loadingFollowers) {
      return;
    }
    const followMsg = this.request.is_following ? 'Unfollowing' : 'Following';
    this.setLoader(`${followMsg} ticket...`);
    try {
      if (await this.store.toggleTicketFollow(this.request)) {
        this.loadActivityLogs();
      }
    }
    finally {
      this.resetLoader();
    }
  }

  async loadFollowers(): Promise<void> {
    this.loadingFollowers = true;
    try {
      const response = await this.apiService.getFollowingUsers(this.feedbackId);
      this.request.followingUsers = Array.isArray(response.result) ? response.result : [];
      this.request.is_following = this.request.followingUsers?.some(follower => follower.user_name.startsWith(this.store.currentUser)) ?? false;
    }
    catch (error) {
      this.request.followingUsers = [];
      throw error;
    }
    finally {
      this.loadingFollowers = false;
    }
  }

  async loadPublicComments(page = 1): Promise<void> {
    await this.loadCommentsByType(true, page);
  }

  async loadTeamThreadComments(page = 1): Promise<void> {
    if (this.canUseTeamThread()) {
      await this.loadCommentsByType(false, page);
    }
  }

  private async loadCommentsByType(isPublic: boolean, page: number): Promise<void> {
    if (isPublic) {
      this.loadingPublicComments = true;
    }
    else {
      this.loadingPrivateNotes = true;
    }
    try {
      const response = await this.apiService.getFeedbackComments(
        this.feedbackId, isPublic,
        page, this.commentsRowsPerPage
      );
      const comments: SupportHubCommentEntry[] = response.result?.records ?? [];
      comments.forEach(comment => comment.isPublic = isPublic);
      const totalPages = Number(response.result?.totalPages) || 1;
      const totalCount = Number(response.result?.totalCount) || 0;
      const currentPage = Number(response.result?.page) || page;

      if (isPublic) {
        this.request.publicComments = comments;
        this.publicCommentsTotalPages = totalPages;
        this.publicCommentsTotalCount = totalCount;
        this.publicCommentsPage = currentPage;
      }
      else {
        this.request.teamThreadComments = comments;
        this.privateCommentsTotalPages = totalPages;
        this.privateCommentsTotalCount = totalCount;
        this.privateCommentsPage = currentPage;
      }
    }
    catch {
      if (isPublic) {
        this.request.publicComments = [];
        this.publicCommentsTotalPages = 1;
        this.publicCommentsTotalCount = 0;
      }
      else {
        this.request.teamThreadComments = [];
        this.privateCommentsTotalPages = 1;
        this.privateCommentsTotalCount = 0;
      }
    }
    finally {
      if (isPublic) {
        this.loadingPublicComments = false;
      }
      else {
        this.loadingPrivateNotes = false;
      }
    }
  }

  addPublicComment() {
    this.createComment(true);
  }

  addTeamThreadNote() {
    this.createComment(false);
  }

  private async createComment(isPublic: boolean): Promise<void> {
    const note = (isPublic ? this.publicComment : this.teamThreadNote).trim();
    if (!this.feedbackId || !note || this.isCommentSectionBusy || (!isPublic && !this.canUseTeamThread())) {
      return;
    }
    this.isCommentActionProgress = true;
    try {
      const response = await this.apiService.addFeedbackComment(this.feedbackId, {
        comment: note,
        is_public: isPublic
      });
      if (response?.message === 'Success' && response.result?.comment_id > 0) {
        this.addCommentToLocalList(isPublic, note, response.result.comment_id);
        if (isPublic) {
          this.publicComment = '';
        }
        else {
          this.teamThreadNote = '';
        }
      }
    }
    finally {
      this.isCommentActionProgress = false;
    }
  }

  private addCommentToLocalList(isPublic: boolean, commentText: string, commentId: number): void {
    const comments = this.getCommentsByType(isPublic);
    const timestamp = new Date().toISOString();
    comments.unshift({
      id: commentId,
      feedback_id: this.feedbackId,
      user_id: 0,
      user_name: this.store.currentUser,
      team_id: isPublic ? null : this.request.assigned_team,
      comment: commentText,
      isPublic: isPublic,
      created_by: this.store.currentUser,
      created_date: timestamp,
      modified_by: this.store.currentUser,
      modified_date: timestamp
    });
    if (comments.length > this.commentsRowsPerPage) {
      comments.pop();
    }
    this.updateCommentCountAndPages(isPublic, 1);
  }

  startEditComment(commentEntry: SupportHubCommentEntry): void {
    if (!this.isCommentSectionBusy) {
      if (commentEntry.isPublic) {
        this.editingPublicCommentId = commentEntry.id;
        this.editingPublicCommentText = commentEntry.comment;
      } else {
        this.editingPrivateCommentId = commentEntry.id;
        this.editingPrivateCommentText = commentEntry.comment;
      }
    }
  }

  cancelEditComment(commentEntry: SupportHubCommentEntry): void {
    if (commentEntry.isPublic) {
      this.editingPublicCommentId = null;
      this.editingPublicCommentText = '';
    } else {
      this.editingPrivateCommentId = null;
      this.editingPrivateCommentText = '';
    }
  }

  async saveEditComment(commentEntry: SupportHubCommentEntry): Promise<void> {
    const commentId = commentEntry.isPublic ? this.editingPublicCommentId : this.editingPrivateCommentId;
    const note = (commentEntry.isPublic ? this.editingPublicCommentText : this.editingPrivateCommentText).trim();
    if (!this.feedbackId || commentId !== commentEntry.id || !note || this.isCommentSectionBusy
      || (!commentEntry.isPublic && !this.canUseTeamThread())) {
      return;
    }
    this.isCommentActionProgress = true;
    try {
      const response = await this.apiService.updateFeedbackComment(
        this.feedbackId, commentEntry.id, { comment: note }
      );
      if (response?.message === 'Success') {
        commentEntry.comment = note;
        commentEntry.modified_by = this.store.currentUser;
        commentEntry.modified_date = new Date().toISOString();
        this.cancelEditComment(commentEntry);
      }
    }
    finally {
      this.isCommentActionProgress = false;
    }
  }

  async deleteComment(commentEntry: SupportHubCommentEntry): Promise<void> {
    if (!this.feedbackId || this.isCommentSectionBusy || (!commentEntry.isPublic && !this.canUseTeamThread())
      || !await this.eventService.confirmDialog('Are you sure you want to delete this comment?')) {
      return;
    }
    this.isCommentActionProgress = true;
    try {
      const response = await this.apiService.deleteFeedbackComment(this.feedbackId, commentEntry.id);
      if (response?.message === 'Success') {
        const editingCommentId = commentEntry.isPublic ? this.editingPublicCommentId : this.editingPrivateCommentId;
        if (editingCommentId === commentEntry.id) {
          this.cancelEditComment(commentEntry);
        }
        const comments = this.getCommentsByType(commentEntry.isPublic);
        const index = comments.findIndex(entry => entry.id === commentEntry.id);
        if (index >= 0) {
          comments.splice(index, 1);
          this.updateCommentCountAndPages(commentEntry.isPublic, -1);
        }
      }
    }
    finally {
      this.isCommentActionProgress = false;
    }
  }

  private getCommentsByType(isPublic: boolean): SupportHubCommentEntry[] {
    return (isPublic ? this.request.publicComments : this.request.teamThreadComments) ?? [];
  }

  private updateCommentCountAndPages(isPublic: boolean, delta: number): void {
    if (isPublic) {
      this.publicCommentsTotalCount = Math.max(0, this.publicCommentsTotalCount + delta);
      this.publicCommentsTotalPages = Math.max(1, Math.ceil(this.publicCommentsTotalCount / this.commentsRowsPerPage));
      this.publicCommentsPage = Math.min(this.publicCommentsPage, this.publicCommentsTotalPages);
      return;
    }
    this.privateCommentsTotalCount = Math.max(0, this.privateCommentsTotalCount + delta);
    this.privateCommentsTotalPages = Math.max(1, Math.ceil(this.privateCommentsTotalCount / this.commentsRowsPerPage));
    this.privateCommentsPage = Math.min(this.privateCommentsPage, this.privateCommentsTotalPages);
  }

  async goToPreviousPublicCommentsPage(): Promise<void> {
    if (this.publicCommentsPage > 1 && !this.isCommentSectionBusy) {
      await this.loadPublicComments(this.publicCommentsPage - 1);
    }
  }

  async goToNextPublicCommentsPage(): Promise<void> {
    if (this.publicCommentsPage < this.publicCommentsTotalPages && !this.isCommentSectionBusy) {
      await this.loadPublicComments(this.publicCommentsPage + 1);
    }
  }

  async goToPreviousPrivateCommentsPage(): Promise<void> {
    if (this.privateCommentsPage > 1 && !this.isCommentSectionBusy) {
      await this.loadTeamThreadComments(this.privateCommentsPage - 1);
    }
  }

  async goToNextPrivateCommentsPage(): Promise<void> {
    if (this.privateCommentsPage < this.privateCommentsTotalPages && !this.isCommentSectionBusy) {
      await this.loadTeamThreadComments(this.privateCommentsPage + 1);
    }
  }

  async loadActivityLogs(page = 1): Promise<void> {
    this.loadingActivityLogs = true;
    try {
      const response = await this.apiService.getFeedbackActivityLogs(
        this.feedbackId, page, this.activityRowsPerPage
      );
      this.request.activityLog = Array.isArray(response.result?.records) ? response.result.records : [];
      this.activityLogsPage = Number(response.result?.page) || page;
      this.activityLogsTotalPages = Number(response.result?.totalPages) || 1;
      this.activityLogsTotalCount = Number(response.result?.totalCount) || 0;
    }
    catch {
      this.request.activityLog = [];
      this.activityLogsPage = 1;
      this.activityLogsTotalPages = 1;
      this.activityLogsTotalCount = 0;
    }
    finally {
      this.loadingActivityLogs = false;
    }
  }

  private async syncClarificationsFromApiData(feedback_id: number): Promise<void> {
    const mappedEntries: clarificationsEntry[] = [];

    const {clarifications, open_count } = await this.store.loadClarificationsList(feedback_id);

    this.buttonRequestClarificationDisabled = open_count >= 1;
    
    if (clarifications.length > 0) {

      clarifications.forEach(clarification => {
        if(clarification.request.user_email === this.user.emailAddress && clarification.response.note === null){
          this.disabledButtonSubmit = true;
        }

        mappedEntries.push({
          clarification_id: clarification.clarification_id,
          is_open: clarification.is_open,
          request: {
            note: clarification.request.note,
            user_name: clarification.request.user_name,
            user_id: clarification.request.user_id,
            date: clarification.request.date,
            user_email: clarification.request.user_email
          },
          response: {
            note: clarification.response.note,
            user_name: clarification.response.user_name,
            user_email: clarification.response.user_email,
            user_id: clarification.response.user_id,
            date: clarification.response.date
          },
          created_by: clarification.created_by,
          created_date: clarification.created_date,
          modified_by: clarification.modified_by,
          modified_date: clarification.modified_date
        });
      });
    }
    if (mappedEntries.length > 0) {
      this.clarificationsEntry = mappedEntries;
      this.updateClarificationsPagination();
    }
  }

  private updateClarificationsPagination(): void {
    this.clarificationsTotalCount = this.clarificationsEntry.length;
    this.clarificationsTotalPages = Math.max(1, Math.ceil(this.clarificationsTotalCount / this.clarificationsRowsPerPage));
    this.clarificationsPage = Math.min(this.clarificationsPage, this.clarificationsTotalPages);
  }

  async goToPreviousActivityLogsPage(): Promise<void> {
    if (this.activityLogsPage > 1 && !this.loadingActivityLogs) {
      await this.loadActivityLogs(this.activityLogsPage - 1);
    }
  }

  async goToNextActivityLogsPage(): Promise<void> {
    if (this.activityLogsPage < this.activityLogsTotalPages && !this.loadingActivityLogs) {
      await this.loadActivityLogs(this.activityLogsPage + 1);
    }
  }

  private async loadValidationUsers(): Promise<void> {
    if (!this.canAssignValidator()) {
      this.validationUsers = [];
      return;
    }
    this.loadingValidationUsers = true;
    try {
      const response = await this.apiService.getFeedbackUsers();
      this.validationUsers = response.result;
      this.prioritizeSubmitter(this.validationUsers);
      this.loadSendNoteUsers();
      await this.loadTeamAssignees();
    }
    finally {
      this.loadingValidationUsers = false;
    }
  }

  private prioritizeSubmitter(users: SupportHubUserLookup[]): void {
    const submitterIndex = users.findIndex(user => user.id === this.request.requested_user);
    if (submitterIndex > 0) {
      const [submitter] = users.splice(submitterIndex, 1);
      users.unshift(submitter);
    }
  }

  private async loadTeamAssignees(): Promise<void> {
    if (!this.canAssignTeamMember()) {
      this.teamAssignees = [];
      return;
    }
    this.loadingTeamAssignees = true;
    try {
      await this.store.loadLookup();
      const developerRoleId = this.store.lookupValue.roles
        .find(role => role.role_name === SupportHubStore.ROLE_DEVELOPER
        && role.usecase_id === this.request.usecase_id)?.role_id;
      // Since we fetch all users for validators, we can filter developers from same list and avoid an API call.
      // const response = await this.apiService.getFeedbackUsers(developerRoleId);
      // this.teamAssignees = response.result;
      this.teamAssignees = this.validationUsers.filter(user => user.role_id === developerRoleId);
    }
    finally {
      this.loadingTeamAssignees = false;
    }
  }

  hasChanges(): boolean {
    if (this.loadingVersions) {
      return false;
    }
    const statusChanged = this.selectedStatusId !== this.initialStatusId && this.canUpdateAnyStatus() && this.selectedStatusId != null;
    const priorityChanged = this.selectedPriorityId !== this.initialPriorityId && this.isAdminRole() && this.selectedPriorityId != null;
    const versionChanged = this.selectedVersionId !== this.initialVersionId && this.isAdminRole() && this.selectedVersionId != null;
    const teamAssigneeChanged = this.teamAssignee !== this.initialAssignedUserId && this.canAssignTeamMember() && this.teamAssignee != null;
    const validationAssigneeChanged = this.validationAssignee !== this.initialValidatedUserId && this.canAssignValidator() && this.validationAssignee != null;

    return statusChanged || priorityChanged || versionChanged || teamAssigneeChanged || validationAssigneeChanged;
  }

  private hasRequiredAdminInProgressFields(): boolean {
    return this.selectedPriorityId != null
      && this.teamAssignee != null;
  }

  async applyAllChanges(): Promise<void> {
    if (!this.feedbackId || this.loader.isLoading || !this.hasChanges()) {
      return;
    }
    if (this.isAdminRole() && this.selectedStatusId === this.store.statusIds.IN_PROGRESS 
      && !this.hasRequiredAdminInProgressFields()) {
      this.eventService.showSnackBar(
        'Priority and Team Assignee are required before moving status to In Progress!', 'error');
      return;
    }
    const payload = this.buildUpdatePayload();
    this.setLoader('Applying changes...');
    try {
      const response = await this.apiService.updateFeedbackEntry(this.feedbackId, payload);
      if (response?.message !== 'Success') {
        this.eventService.showSnackBar(response?.error ?? 'Unable to apply changes. Please try again !', 'error');
        return;
      }
      this.applyUpdatedFields(payload);
      this.syncInitialChangeState();
      this.eventService.showSnackBar('Changes applied successfully !', 'success');
      this.loadActivityLogs();
    }
    catch (error: any) {
      this.eventService.showSnackBar(error.message ?? 'Unable to apply changes. Please try again !', 'error');
    }
    finally {
      this.resetLoader();
    }
  }

  private buildUpdatePayload(): Record<string, number> {
    const payload: Record<string, number> = {};

    
    const statusChanged = this.canUpdateAnyStatus()
      && this.selectedStatusId != null
      && this.selectedStatusId !== this.initialStatusId;

    const priorityChanged = this.isAdminRole()
      && this.selectedPriorityId != null
      && this.selectedPriorityId !== this.initialPriorityId;

    const versionChanged = this.isAdminRole()
      && this.selectedVersionId != null
      && this.selectedVersionId !== this.initialVersionId;

    const teamAssigneeChanged = this.canAssignTeamMember()
      && this.teamAssignee != null
      && this.teamAssignee !== this.initialAssignedUserId;

    const validationAssigneeChanged = this.canAssignValidator()
      && this.validationAssignee != null
      && this.validationAssignee !== this.initialValidatedUserId;

    if (statusChanged) {
      const statusId = this.selectedStatusId as number;
      payload.status_id = statusId;
    }

    if (priorityChanged) {
      const priorityId = this.selectedPriorityId as number;
      payload.priority_id = priorityId;
    }

    if (versionChanged) {
      const versionId = this.selectedVersionId as number;
      payload.version_id = versionId;
    }

    if (teamAssigneeChanged) {
      const assignedUser = this.teamAssignee as number;
      payload.assigned_user = assignedUser;
      //Set contractor_user same as assigned_user when setting to in progress
      if (this.request.status_id === this.store.statusIds.NEW
        && this.selectedStatusId === this.store.statusIds.IN_PROGRESS) {
        payload.contractor_user = assignedUser;
      }
    }

    if (validationAssigneeChanged) {
      const validatedUser = this.validationAssignee as number;
      payload.validated_user = validatedUser;
    }

    return payload;
  }

  private applyUpdatedFields(payload: Record<string, number>): void {
    if (payload.status_id != null) {
      this.request.status_id = payload.status_id;
      this.selectedStatusId = payload.status_id;
    }
    if (payload.priority_id != null) {
      this.request.priority_id = payload.priority_id;
      this.selectedPriorityId = payload.priority_id;
    }
    if (payload.version_id != null) {
      this.request.version_id = payload.version_id;
      this.request.version = this.versionsList.find(v => v.version_id === payload.version_id)?.version_name;
      this.selectedVersionId = payload.version_id;
    }
    if (payload.assigned_user != null) {
      this.request.assigned_user = payload.assigned_user;
      const selectedAssignee = this.teamAssignees.find(user => user.id === payload.assigned_user);
      this.request.assigned_user_name = selectedAssignee?.user_name;
      this.teamAssignee = payload.assigned_user;
    }
    if (payload.validated_user != null) {
      this.request.validated_user = payload.validated_user;
      const selectedValidator = this.validationUsers.find(user => user.id === payload.validated_user);
      this.request.validated_user_name = selectedValidator?.user_name;
      this.validationAssignee = payload.validated_user;
    }
  }

  private syncInitialChangeState(): void {
    this.initialStatusId = this.request.status_id;
    this.initialPriorityId = this.request.priority_id;
    this.initialVersionId = this.request.version_id;
    this.initialAssignedUserId = this.request.assigned_user;
    this.initialValidatedUserId = this.request.validated_user;
  }

  resetAllChanges(): void {
    this.selectedStatusId = this.initialStatusId;
    this.selectedPriorityId = this.initialPriorityId;
    this.selectedVersionId = this.initialVersionId;
    this.teamAssignee = this.initialAssignedUserId;
    this.validationAssignee = this.initialValidatedUserId;
  }

  backToList() {
    this.router.navigate(['/support-hub/all-tickets'], {
      queryParams: this.store.getUseCaseQueryParams()
    });
  }

  editTicket() {
    if (this.feedbackId && this.canEditTicket()) {
      this.router.navigate(['/support-hub/request/edit', this.feedbackId]);
    }
  }

  onAttachmentRemoved(event: FileRemoveEvent): void {
    //Update feedbackFiles array if files are locally removed from file upload component
    this.feedbackFiles = this.feedbackFiles.filter(f => f.file !== event.file);
  }

  async onAttachmentSelected(event: FileSelectEvent): Promise<void> {
    if (!this.loader.isLoading && event.files.length > 0 && this.canManageAttachments()) {
      this.setLoader('Uploading attachments...');
      try {
        await this.store.handleAttachments(this.fileUploader, event.files, this.feedbackFiles, this.feedbackId);
        await this.loadAttachmentPreviews();
        this.loadActivityLogs();
      }
      finally {
        this.resetLoader();
      }
    }
  }

  async downloadAttachment(index: number): Promise<void> {
    if (!this.loader.isLoading && this.feedbackFiles[index]) {
      this.setLoader('Downloading attachment...');
      try {
        const fileInfo = this.feedbackFiles[index];
        if (fileInfo.file) {
          const fileUrl = URL.createObjectURL(fileInfo.file);
          this.triggerBrowserDownload(fileUrl, fileInfo.file_name);
          URL.revokeObjectURL(fileUrl);
          this.eventService.showSnackBar(`Downloaded attachment '${fileInfo.file_name}' successfully !`, 'success');
          return;
        }

        if (!fileInfo.id) {
          this.eventService.showSnackBar('Download failed, Please try again !', 'error');
          return;
        }

        const downloadUrl = await this.getAttachmentDownloadUrl(fileInfo.id);
        if (!downloadUrl) {
          this.eventService.showSnackBar('Download failed, Please try again !', 'error');
          return;
        }

        this.triggerBrowserDownload(downloadUrl, fileInfo.file_name);
        this.eventService.showSnackBar(`Downloaded attachment '${fileInfo.file_name}' successfully !`, 'success');
      }
      catch {
        this.eventService.showSnackBar('Download failed, Please try again !', 'error');
      }
      finally {
        this.resetLoader();
      }
    }
  }

  async deleteAttachment(index: number): Promise<void> {
    if (!this.loader.isLoading && this.feedbackFiles[index]) {
      this.setLoader('Deleting attachment...');
      try {
        const fileInfo = this.feedbackFiles[index];
        if (await this.store.deleteAttachment(fileInfo)) {
          if (fileInfo.id) {
            this.attachmentPreviewUrlById.delete(fileInfo.id);
          }
          const localKey = this.getLocalFilePreviewKey(fileInfo);
          if (localKey) {
            const localUrl = this.attachmentPreviewUrlByLocalKey.get(localKey);
            if (localUrl) {
              URL.revokeObjectURL(localUrl);
              this.attachmentPreviewUrlByLocalKey.delete(localKey);
            }
          }
          this.feedbackFiles = this.feedbackFiles.filter((_, i) => i !== index);
          this.loadActivityLogs();
        }
      }
      finally {
        this.resetLoader();
      }
    }
  }

  getAttachmentPreviewUrl(fileInfo: FeedbackFileInfo): string | null {
    if (fileInfo.id && this.attachmentPreviewUrlById.has(fileInfo.id)) {
      return this.attachmentPreviewUrlById.get(fileInfo.id) ?? null;
    }
    const localKey = this.getLocalFilePreviewKey(fileInfo);
    if (localKey && this.attachmentPreviewUrlByLocalKey.has(localKey)) {
      return this.attachmentPreviewUrlByLocalKey.get(localKey) ?? null;
    }
    return null;
  }

  isImageAttachment(fileInfo: FeedbackFileInfo): boolean {
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(fileInfo.file_name);
  }

  private triggerBrowserDownload(downloadUrl: string, fileName: string): void {
    const anchor = this.document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = fileName;
    anchor.target = '_blank';
    anchor.rel = 'noopener';
    this.document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }

  private async loadAttachmentPreviews(): Promise<void> {
    const previewLoads = this.feedbackFiles
      .filter(file => this.isImageAttachment(file))
      .map(file => this.ensureAttachmentPreviewUrl(file));

    if (previewLoads.length > 0) {
      await Promise.allSettled(previewLoads);
    }
  }

  private async ensureAttachmentPreviewUrl(fileInfo: FeedbackFileInfo): Promise<string | null> {
    if (!this.isImageAttachment(fileInfo)) {
      return null;
    }

    const existingUrl = this.getAttachmentPreviewUrl(fileInfo);
    if (existingUrl) {
      return existingUrl;
    }

    if (fileInfo.file) {
      const localKey = this.getLocalFilePreviewKey(fileInfo);
      if (!localKey) {
        return null;
      }
      const localUrl = URL.createObjectURL(fileInfo.file);
      this.attachmentPreviewUrlByLocalKey.set(localKey, localUrl);
      return localUrl;
    }

    if (!fileInfo.id) {
      return null;
    }

    const downloadUrl = await this.getAttachmentDownloadUrl(fileInfo.id);
    if (downloadUrl) {
      this.attachmentPreviewUrlById.set(fileInfo.id, downloadUrl);
      return downloadUrl;
    }
    return null;
  }

  private async getAttachmentDownloadUrl(fileId: number): Promise<string | null> {
    if (this.attachmentPreviewUrlById.has(fileId)) {
      return this.attachmentPreviewUrlById.get(fileId) ?? null;
    }
    const response = await this.apiService.downloadFeedbackFile(fileId);
    const downloadUrl: string | undefined = response?.result?.url;
    if (!downloadUrl) {
      return null;
    }
    this.attachmentPreviewUrlById.set(fileId, downloadUrl);
    return downloadUrl;
  }

  private getLocalFilePreviewKey(fileInfo: FeedbackFileInfo): string | null {
    if (!fileInfo.file) {
      return null;
    }
    return `${fileInfo.file_name}:${fileInfo.file.size}:${fileInfo.file.lastModified}`;
  }

  ngOnDestroy(): void {
    for (const localUrl of this.attachmentPreviewUrlByLocalKey.values()) {
      URL.revokeObjectURL(localUrl);
    }
    this.attachmentPreviewUrlByLocalKey.clear();
    this.attachmentPreviewUrlById.clear();
  }

  private setLoader(message: string): void {
    this.loader.isLoading = true;
    this.loader.message = message;
  }

  private resetLoader(): void {
    this.loader.isLoading = false;
    this.loader.message = null;
  }
}